import { parseQueryString, getFullUrlPath } from '../paramsFormat.js'
import _get from 'lodash/get'

// Blank popup pre-opened in the click gesture so iOS Safari doesn't block it; the SSO flow reuses it (fixes the "tap twice" bug). Tagged with its name so an unclaimed pre-open can't be reused by an unrelated popup (e.g. LINE login).
let preOpenedPopup = null

// Close and drop any unclaimed pre-opened window.
function discardPreOpenedPopup() {
  if (!preOpenedPopup) return
  try {
    if (!preOpenedPopup.win.closed) preOpenedPopup.win.close()
  } catch (e) {
    // ignore
  }
  preOpenedPopup = null
}

// Pre-open a blank popup — must be called synchronously in a user click gesture.
export function preOpenOAuthPopup(name = 'OpenId Connect') {
  if (typeof window === 'undefined') return
  discardPreOpenedPopup() // never keep more than one around
  try {
    const win = window.open('about:blank', name)
    preOpenedPopup = win ? { win, name } : null
  } catch (e) {
    preOpenedPopup = null
  }
}

/**
 * OAuth2 popup management class
 *
 * @author Sahat Yalkabov <https://github.com/sahat>
 * @copyright Class mostly taken from https://github.com/sahat/satellizer
 * and adjusted to fit vue-authenticate library
 */
export default class OAuthPopup {
  constructor(url, name, popupOptions) {
    this.popup = null
    this.url = url
    this.name = name
    this.popupOptions = popupOptions
  }

  open(redirectUri, skipPooling) {
    try {
      // Reuse the popup pre-opened in the click gesture (name match), so iOS Safari doesn't block the window we open after an await/navigation.
      if (preOpenedPopup && !preOpenedPopup.win.closed && preOpenedPopup.name === this.name) {
        this.popup = preOpenedPopup.win
        preOpenedPopup = null
        this.popup.location.href = this.url
      } else {
        this.popup = window.open(this.url, this.name, this._stringifyOptions())
      }
      if (this.popup && this.popup.focus) {
        this.popup.focus()
      }

      if (skipPooling) {
        return Promise.resolve()
      } else {
        return this.pooling(redirectUri)
      }
    } catch (e) {
      return Promise.reject(new Error('OAuth popup error occurred'))
    }
  }

  pooling(redirectUri) {
    return new Promise((resolve, reject) => {
      const redirectUriParser = document.createElement('a')
      redirectUriParser.href = redirectUri
      const redirectUriPath = getFullUrlPath(redirectUriParser)

      let poolingInterval = setInterval(() => {
        if (!this.popup || this.popup.closed || this.popup.closed === undefined) {
          clearInterval(poolingInterval)
          poolingInterval = null
          reject(new Error('Auth popup window closed'))
        }

        try {
          const popupWindowPath = getFullUrlPath(this.popup.location)

          if (popupWindowPath === redirectUriPath) {
            if (this.popup.location.search || this.popup.location.hash) {
              const query = parseQueryString(
                this.popup.location.search.substring(1).replace(/\/$/, '')
              )
              const hash = parseQueryString(
                this.popup.location.hash.substring(1).replace(/[/$]/, '')
              )
              const params = { ...query, ...hash }

              if (params.error) {
                reject(new Error(params.error))
              } else {
                resolve(params)
              }
            } else {
              reject(
                new Error('OAuth redirect has occurred but no query or hash parameters were found.')
              )
            }

            clearInterval(poolingInterval)
            poolingInterval = null
            this.popup.close()
          }
        } catch (e) {
          // Ignore DOMException: Blocked a frame with origin from accessing a cross-origin frame.
        }
      }, 250)
    })
  }

  _stringifyOptions() {
    const options = []
    for (const optionKey in this.popupOptions) {
      if (_get(this.popupOptions, optionKey)) {
        options.push(`${optionKey}=${this.popupOptions[optionKey]}`)
      }
    }
    return options.join(',')
  }
}
